import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils/src/index.tsx';
import { CollapsibleSection } from '@atlassian/jira-software-releases-collapsible-ui-toggle/src/ui/index.tsx';
import type { ui_softwareReleasesVersionProgressQuery } from './__generated__/ui_softwareReleasesVersionProgressQuery.graphql';
import type { ui_versionProgress_VersionProgress$key } from './__generated__/ui_versionProgress_VersionProgress.graphql';
import { IssuesProgressBar } from './issue-progress-bar/index.tsx';
import { IssueProgressDetails } from './issue-progress-details/index.tsx';
import { IssuesHeading } from './issues-heading/index.tsx';
import { messages } from './messages.tsx';
import { RelatedWorkHeading } from './related-work-heading/index.tsx';
import { RelatedWorkProgressBar } from './related-work-progress-bar/index.tsx';

type Props = {
	queryRef: PreloadedQuery<ui_softwareReleasesVersionProgressQuery>;
};

export const VersionProgress = ({ queryRef }: Props): React.JSX.Element => {
	const { jira } = usePreloadedQuery<ui_softwareReleasesVersionProgressQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query ui_softwareReleasesVersionProgressQuery(
				$versionAri: ID!
				$isDevopsFeatureDisabledInFedRamp: Boolean!
			) {
				jira @required(action: THROW) {
					version(id: $versionAri) @required(action: THROW) {
						...ui_versionProgress_VersionProgress
					}
				}
			}
		`,
		queryRef,
	);
	const versionFrag: ui_versionProgress_VersionProgress$key = jira.version;

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_versionProgress_VersionProgress on JiraVersion {
				relatedWorkV2(first: 100)
					@connection(key: "version_relatedWorkV2")
					@required(action: THROW)
					@skip(if: $isDevopsFeatureDisabledInFedRamp) {
					edges @required(action: THROW) {
						...relatedWorkHeading_versionProgress
						...relatedWorkProgressBar_versionProgress
					}
				}
				...issuesHeading_versionProgress
				...issueProgressBar_versionProgress_IssuesProgressBar
				...issueProgressDetails_versionProgress
				...ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection
			}
		`,
		versionFrag,
	);

	const { formatMessage } = useIntl();
	const edges = version.relatedWorkV2 ? version.relatedWorkV2.edges.filter(Boolean) : [];

	return (
		<Box xcss={containerStyles}>
			<Box xcss={mainHeadingStyles}>
				<Heading size="xsmall" as="h2">
					{formatMessage(messages.progressHeading)}
				</Heading>
			</Box>
			{!isDevopsFeatureDisabledInFedRamp() && (
				<Box>
					<RelatedWorkHeading relatedWorkEdges={edges} />
					<RelatedWorkProgressBar relatedWorkEdges={edges} />
				</Box>
			)}
			<Box>
				<CollapsibleSection
					jiraVersion={version}
					uiType="PROGRESS_CARD"
					renderHeader={(isCollapsed, onToggleClicked) => (
						<>
							<IssuesHeading
								jiraVersion={version}
								isCollapsed={isCollapsed}
								onToggleClicked={onToggleClicked}
							/>
							<IssuesProgressBar jiraVersion={version} />
						</>
					)}
					renderBody={() => <IssueProgressDetails jiraVersion={version} />}
				/>
			</Box>
		</Box>
	);
};

const containerStyles = xcss({
	paddingTop: 'space.200',
	paddingRight: 'space.300',
	paddingBottom: 'space.300',
	paddingLeft: 'space.300',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.200',
});

const mainHeadingStyles = xcss({
	paddingTop: 'space.100',
	paddingRight: 'space.0',
	paddingBottom: 'space.100',
	paddingLeft: 'space.0',
});
